import { X, Mail, Users, Calendar, Send } from "lucide-react";
import React, { useEffect } from "react";
import DOMPurify from 'dompurify';

// Utilidades
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('es-ES', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const EmailModal = ({ email, onClose }) => {
  // Cerrar con ESC
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div 
        className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl transform transition-all duration-300 ease-out animate-in"
        style={{
          animation: 'modalSlideIn 0.3s ease-out'
        }}
      >
        {/* Header del Modal */}
        <div className="bg-gradient-to-r from-[#0488AF] to-[#09274A] p-6 text-white">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                <Mail className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Detalles del Correo</h3>
                <p className="text-blue-100 text-sm mt-1">Información completa del mensaje</p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="text-white hover:text-red-200 hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-all duration-200 transform hover:scale-110"
              title="Cerrar (ESC)"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        {/* Contenido del Modal */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 120px)' }}>
          <div className="space-y-6">
            
            {/* Asunto */}
            <div className="bg-gray-50 p-4 rounded-xl border-l-4 border-[#0488AF]">
              <label className="block text-sm font-semibold text-[#09274A] mb-2 uppercase tracking-wide">
                Asunto
              </label>
              <p className="text-lg font-semibold text-[#09274A] leading-relaxed">
                {email.subject}
              </p>
              {email.type && (
                <span className="inline-block mt-2 bg-[#0488AF] bg-opacity-10 text-[#0488AF] text-sm font-medium px-3 py-1.5 rounded-full">
                  {email.type}
                </span>
              )}
            </div>
            
            {/* Destinatarios Para */}
            <div className="bg-blue-50 p-4 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-5 h-5 text-[#0488AF]" />
                <label className="text-sm font-semibold text-[#09274A] uppercase tracking-wide">
                  Para ({email.to.length} destinatario{email.to.length !== 1 ? 's' : ''})
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                {email.to.map((recipient, index) => (
                  <span 
                    key={index} 
                    className="bg-[#0488AF] bg-opacity-10 text-[#0488AF] text-sm font-medium px-3 py-2 rounded-lg hover:bg-opacity-20 transition-colors duration-200 border border-[#0488AF] border-opacity-20"
                  >
                    {recipient}
                  </span>
                ))}
              </div>
            </div>
            
            {/* CC */}
            {email.cc?.length > 0 && (
              <div className="bg-slate-50 p-4 rounded-xl">
                <label className="block text-sm font-semibold text-[#09274A] mb-3 uppercase tracking-wide">
                  CC ({email.cc.length})
                </label>
                <div className="flex flex-wrap gap-2">
                  {email.cc.map((recipient, index) => (
                    <span 
                      key={index} 
                      className="bg-[#09274A] bg-opacity-10 text-[#09274A] text-sm font-medium px-3 py-2 rounded-lg hover:bg-opacity-20 transition-colors duration-200 border border-[#09274A] border-opacity-20"
                    >
                      {recipient}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* BCC */}
            {email.bcc?.length > 0 && (
              <div className="bg-gray-50 p-4 rounded-xl">
                <label className="block text-sm font-semibold text-[#09274A] mb-3 uppercase tracking-wide">
                  BCC ({email.bcc.length})
                </label>
                <div className="flex flex-wrap gap-2">
                  {email.bcc.map((recipient, index) => (
                    <span 
                      key={index} 
                      className="bg-gray-200 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg hover:bg-gray-300 transition-colors duration-200"
                    >
                      {recipient}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Mensaje */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-5 rounded-xl border">
              <label className="block text-sm font-semibold text-[#09274A] mb-3 uppercase tracking-wide">
                Mensaje
              </label>
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <p className="text-gray-800 whitespace-pre-wrap leading-relaxed text-base">
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(email.content) }} />
                </p>
              </div>
            </div>
            
            {/* Footer con metadatos */}
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex flex-col sm:flex-row justify-between gap-4 text-sm">
                    <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-[#0488AF] bg-opacity-10 rounded-lg">
                        <Send className="w-4 h-4 text-[#0488AF]" />
                    </div>
                    <span className="text-gray-600">Tipo:</span>
                    <span className="text-[#0488AF] font-semibold">
                        {email.type || 'Sin especificar'}
                    </span>
                    </div>
                    <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-[#09274A] bg-opacity-10 rounded-lg">
                        <Calendar className="w-4 h-4 text-[#09274A]" />
                    </div>
                    <span className="text-gray-600">Enviado:</span>
                    <span className="text-[#09274A] font-semibold">
                        {formatDate(email.createdAt)}
                    </span>
                    </div>
                </div>
            </div>

            
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default EmailModal;